import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, CheckCircle, XCircle, Loader2, MapPin, Users, Car, Compass, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

declare global {
  interface Window {
    paypal?: any;
  }
}

interface TransferPaymentState {
  type?: "transfer";
  bookingId: string;
  bookingCode: string;
  from: string;
  to: string;
  passengers: number;
  routeType: string;
  totalPrice: number;
  pickupDate: string;
  returnDate: string | null;
  distanceKm: number | null;
  estimatedTimeMin: number | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  flightNumber: string | null;
  specialRequests: string | null;
  paymentMethod: string;
  vehicle: {
    name: string;
    image_url: string | null;
  };
}

interface TourPaymentState {
  type: "tour";
  bookingId: string;
  bookingCode: string;
  tourTitle: string;
  tourImage: string | null;
  destinationTitle: string;
  excursionDate: string;
  schedule: string | null;
  adults: number;
  children: number;
  pricingMode: string;
  selectedPackageName: string | null;
  totalPrice: number;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  pickupPoint: string;
  comments: string;
  paymentMethod: string;
}

type PaymentState = TransferPaymentState | TourPaymentState;
type PaymentStatus = "loading" | "ready" | "processing" | "success" | "cancelled" | "error";

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as PaymentState | null;

  const [status, setStatus] = useState<PaymentStatus>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  const buttonsRendered = useRef(false);

  const isTour = state?.type === "tour";
  const totalPrice = state?.totalPrice ?? 0;

  useEffect(() => {
    if (!state) return;

    let scriptEl: HTMLScriptElement | null = null;

    const loadPayPal = async () => {
      const { data: pm } = await supabase
        .from("payment_methods")
        .select("client_id, environment")
        .eq("method", "paypal")
        .eq("is_active", true)
        .single();

      if (!pm?.client_id) {
        setStatus("error");
        setErrorMsg("PayPal is not configured. Please contact support.");
        return;
      }

      const existing = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (existing) existing.remove();

      scriptEl = document.createElement("script");
      scriptEl.src = `https://www.paypal.com/sdk/js?client-id=${pm.client_id}&currency=USD`;
      scriptEl.async = true;
      scriptEl.onload = () => {
        if (!buttonsRendered.current && window.paypal && paypalContainerRef.current) {
          buttonsRendered.current = true;
          renderButtons();
        }
      };
      scriptEl.onerror = () => {
        setStatus("error");
        setErrorMsg("Failed to load PayPal. Please try again.");
      };
      document.body.appendChild(scriptEl);
    };

    const renderButtons = () => {
      setStatus("ready");

      const description = isTour
        ? `Tour Booking ${state.bookingCode}`
        : `Transfer Booking ${(state as TransferPaymentState).bookingCode}`;

      window.paypal.Buttons({
        style: { layout: "vertical", shape: "rect", label: "pay" },
        createOrder: (_data: any, actions: any) => {
          return actions.order.create({
            purchase_units: [{
              description,
              amount: { value: totalPrice.toFixed(2), currency_code: "USD" },
            }],
          });
        },
        onApprove: async (_data: any, actions: any) => {
          setStatus("processing");
          try {
            const details = await actions.order.capture();
            const table = isTour ? "tour_bookings" : "bookings";

            const { error } = await supabase
              .from(table)
              .update({
                status: "confirmed",
                payment_status: "paid",
                stripe_payment_id: details.id,
              } as any)
              .eq("id", state.bookingId);

            if (error) {
              console.error(error);
              setStatus("error");
              setErrorMsg("Payment captured but failed to update booking. Please contact support.");
              return;
            }

            // Send confirmation email
            if (isTour) {
              supabase.functions.invoke("send-tour-booking-email", {
                body: { booking_id: state.bookingId },
              }).catch((err) => console.error("Tour email send error:", err));
            } else {
              supabase.functions.invoke("send-booking-email", {
                body: { booking_id: state.bookingId },
              }).catch((err) => console.error("Email send error:", err));
            }

            setStatus("success");
            toast.success("Payment successful!");

            setTimeout(() => {
              if (isTour) {
                const ts = state as TourPaymentState;
                navigate("/tour-confirmation", {
                  state: {
                    booking: {
                      id: ts.bookingId,
                      booking_code: ts.bookingCode,
                      tour_id: null,
                      excursion_date: ts.excursionDate,
                      schedule: ts.schedule,
                      adults: ts.adults,
                      children: ts.children,
                      pricing_mode: ts.pricingMode,
                      total_price: ts.totalPrice,
                      customer_name: ts.customerName,
                      customer_email: ts.customerEmail,
                      customer_phone: ts.customerPhone,
                      pickup_point: ts.pickupPoint,
                      comments: ts.comments,
                      payment_method: "paypal",
                      payment_status: "paid",
                      status: "confirmed",
                    },
                    tourTitle: ts.tourTitle,
                    tourImage: ts.tourImage,
                    destinationTitle: ts.destinationTitle,
                    duration: null,
                    pricingMode: ts.pricingMode,
                    selectedPackageName: ts.selectedPackageName,
                    adultPrice: null,
                    childPrice: null,
                  },
                });
              } else {
                const tf = state as TransferPaymentState;
                navigate("/confirmation", {
                  state: {
                    booking: {
                      id: tf.bookingId,
                      booking_code: tf.bookingCode,
                      pickup_location: tf.from,
                      dropoff_location: tf.to,
                      passengers: tf.passengers,
                      route_type: tf.routeType,
                      total_price: tf.totalPrice,
                      pickup_date: tf.pickupDate,
                      return_date: tf.returnDate,
                      distance_km: tf.distanceKm,
                      estimated_time_min: tf.estimatedTimeMin,
                      customer_name: tf.customerName,
                      customer_email: tf.customerEmail,
                      customer_phone: tf.customerPhone,
                      flight_number: tf.flightNumber,
                      special_requests: tf.specialRequests,
                      payment_method: "paypal",
                      payment_status: "paid",
                      status: "confirmed",
                      stripe_payment_id: details.id,
                    },
                    vehicle: tf.vehicle,
                  },
                });
              }
            }, 1500);
          } catch (err) {
            console.error(err);
            setStatus("error");
            setErrorMsg("An error occurred capturing the payment.");
          }
        },
        onCancel: () => setStatus("cancelled"),
        onError: (err: any) => {
          console.error("PayPal error:", err);
          setStatus("error");
          setErrorMsg("PayPal encountered an error. Please try again.");
        },
      }).render(paypalContainerRef.current);
    };

    loadPayPal();

    return () => {
      if (scriptEl) scriptEl.remove();
      buttonsRendered.current = false;
    };
  }, [state]);

  if (!state) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">No payment information found.</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-hero-bg py-4 border-b border-hero-foreground/10">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <a href="/" className="text-2xl font-display font-bold text-hero-foreground">
            Taxi<span className="text-primary">UB</span>
          </a>
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-hero-foreground/70 hover:text-primary">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="text-center space-y-2">
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">
              Complete Your <span className="text-primary">Payment</span>
            </h1>
            <p className="text-muted-foreground">Secure payment powered by PayPal</p>
          </div>

          {/* Summary Card */}
          <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Booking Code</p>
                <p className="font-display font-bold text-foreground text-lg">{state.bookingCode}</p>
              </div>
              <Badge variant="secondary" className="text-xs">
                {isTour ? "Tour" : (state as TransferPaymentState).routeType === "round-trip" ? "Round Trip" : "One Way"}
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {isTour ? (
                <>
                  <div className="flex items-start gap-2">
                    <Compass className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Tour</p>
                      <p className="text-foreground font-medium">{(state as TourPaymentState).tourTitle}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="text-foreground font-medium">
                        {format(new Date((state as TourPaymentState).excursionDate), "PPP")}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Pick-up</p>
                      <p className="text-foreground font-medium">{(state as TransferPaymentState).from}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Drop-off</p>
                      <p className="text-foreground font-medium">{(state as TransferPaymentState).to}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{(state as TransferPaymentState).passengers} passenger(s)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{(state as TransferPaymentState).vehicle.name}</span>
                  </div>
                </>
              )}
            </div>

            <div className="pt-4 border-t border-border flex items-end justify-between">
              <p className="text-muted-foreground text-sm">Total</p>
              <p className="text-3xl font-display font-bold text-primary">${totalPrice.toFixed(2)}</p>
            </div>
          </div>

          {/* Payment Section */}
          {status === "success" ? (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="p-8 rounded-2xl bg-card border border-border text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-primary mx-auto" />
              <h2 className="font-display text-xl font-bold text-foreground">Payment Successful!</h2>
              <p className="text-muted-foreground">Redirecting to your confirmation...</p>
            </motion.div>
          ) : status === "cancelled" ? (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="p-8 rounded-2xl bg-card border border-border text-center space-y-4">
              <XCircle className="w-16 h-16 text-accent mx-auto" />
              <h2 className="font-display text-xl font-bold text-foreground">Payment Cancelled</h2>
              <p className="text-muted-foreground">Your booking is still saved. You can try again.</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => { setStatus("ready"); buttonsRendered.current = false; window.location.reload(); }}>Try Again</Button>
                <Button variant="outline" onClick={() => navigate("/")}>Go Home</Button>
              </div>
            </motion.div>
          ) : status === "error" ? (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="p-8 rounded-2xl bg-card border border-border text-center space-y-4">
              <XCircle className="w-16 h-16 text-destructive mx-auto" />
              <h2 className="font-display text-xl font-bold text-foreground">Payment Error</h2>
              <p className="text-muted-foreground">{errorMsg}</p>
              <Button variant="outline" onClick={() => navigate("/")}>Go Home</Button>
            </motion.div>
          ) : (
            <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <h2 className="font-display text-lg font-bold text-foreground">Choose Payment Method</h2>
              </div>
              {(status === "loading" || status === "processing") && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground">
                    {status === "processing" ? "Processing payment..." : "Loading PayPal..."}
                  </span>
                </div>
              )}
              <div ref={paypalContainerRef} className={status === "loading" || status === "processing" ? "hidden" : ""} />
            </div>
          )}

          {status !== "success" && (
            <p className="text-center text-xs text-muted-foreground">
              🔒 Your payment is processed securely by PayPal. We never store your card details.
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentPage;
